import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { LocalDateTime } from "@js-joda/core";
import React from "react";
import { CaregiverBankInformation, InstaPayVisit } from "../../messages";
import { STRINGS } from "../../strings";
import { globalize, translate } from "../../translate";
import { HashSet } from "../../utils/HashSet";
import Flex from "../Generic/Flex";
import Margin from "../Margin";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import PayrollButton from "./components/PayrollButton";
import SelectEligibleVisitsCard from "./components/SelectEligibleVisitsCard";

export type EligibleInstaPayVisit = InstaPayVisit & {
  status: {
    type: "EligibleInstaPay";
    eligibleAmount: number;
  };
};

type Props = {
  visits: EligibleInstaPayVisit[];
  bankInformation: CaregiverBankInformation | null;
  onClickContinue: (params: {
    visits: HashSet<InstaPayVisit>;
    bankInformation: CaregiverBankInformation | null;
  }) => void;
  onClickGoBack: () => void;
};

export default function SelectInstaPayVisitsScreen(props: Props) {
  const [selectedVisits, setSelectedVisits] = React.useState<HashSet<EligibleInstaPayVisit>>(() =>
    HashSet.fromCollection(props.visits)
  );

  const handleClickVisit = (visit: EligibleInstaPayVisit) => {
    setSelectedVisits((prevSelectedVisits) => {
      return prevSelectedVisits.has(visit)
        ? prevSelectedVisits.delete(visit)
        : prevSelectedVisits.add(visit);
    });
  };

  const handleClickContinue = () => {
    return props.onClickContinue({
      visits: selectedVisits,
      bankInformation: props.bankInformation,
    });
  };

  return (
    <Scaffold
      title={<ScaffoldTitle label={translate(STRINGS.INSTAPAY)} />}
      footer={<PayrollButton selectedVisits={selectedVisits} onClick={handleClickContinue} />}
      onClickGoBack={props.onClickGoBack}
    >
      <Margin m={16}>
        <SelectEligibleVisitsCard
          visits={props.visits}
          selected={selectedVisits}
          onChange={handleClickVisit}
        />
      </Margin>
    </Scaffold>
  );
}

export const MotionFlex = motion(Flex);

export const VisitCard = styled.div<{ isSelected: boolean }>`
  --border-color: ${(props) => (props.isSelected ? "var(--primary-color)" : "var(--tw-slate-200)")};
  --background-color: ${(props) => (props.isSelected ? "var(--primary-softest-color)" : "white")};
  --box-shadow: ${(props) => (props.isSelected ? "0 0 0 1px var(--primary-color) inset" : "none")};

  background-color: var(--background-color);
  border: 1px solid var(--border-color);
  box-shadow: var(--box-shadow);
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease-in-out;
`;

export const VisitPrice = styled.span`
  font-weight: 700;
  font-size: 18px;
`;

export const VisitDate = styled.strong`
  font-weight: 700;
`;

export const VisitTimeRange = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--tw-slate-600);
`;

export function formatCentsToDollars(cents: number) {
  const formatted = (cents / 100).toFixed(2);
  return "$" + (formatted.endsWith(".00") ? formatted.slice(0, -3) : formatted);
}

export function formatVisitTimeRange(
  visit: Pick<InstaPayVisit, "startTime" | "endTime" | "clockInTime" | "clockOutTime">
) {
  const start = formatToTime(visit.clockInTime ?? visit.startTime);
  const end = formatToTime(visit.clockOutTime ?? visit.endTime);

  return `${start} - ${end}`;
}

export function formatToDate(value: LocalDateTime) {
  return globalize().formatLocalDateTime(value, { skeleton: "Md" });
}

function formatToTime(value: LocalDateTime) {
  return globalize().formatLocalDateTime(value, { time: "short" });
}
